import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SegmentedField, BooleanField, SelectField } from '../shared/FormField';
import { SPECIES_CATEGORIES, MANDATE_STATUSES } from '../data/enums';
import { parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

const blank = {
  species_code: '',
  common_name_nigeria: '',
  scientific_name: '',
  species_category: '',
  sex_disaggregation_required: null,
  mandate_status: '',
};

export default function CreateSpeciesModal({ isOpen, onClose, onCreate }) {
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    if (submitting) return;

    if (
      !form.species_code.trim() ||
      !form.common_name_nigeria.trim() ||
      !form.scientific_name.trim() ||
      !form.species_category ||
      form.sex_disaggregation_required === null ||
      !form.mandate_status
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onCreate({
        species_code: form.species_code.trim(),
        common_name_nigeria: form.common_name_nigeria.trim(),
        scientific_name: form.scientific_name.trim(),
        species_category: form.species_category,
        sex_disaggregation_required: form.sex_disaggregation_required,
        mandate_status: form.mandate_status,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to create species.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(blank);
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title="Create Species Registry"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Create"
      successTitle="Species Created"
      successMessage="The species record was saved to the backend as a draft."
      isSubmitting={submitting}
    >
      <TextField
        label="Species Code"
        required
        value={form.species_code}
        onChange={set('species_code')}
        placeholder="e.g. new_species"
        note="Lowercase, underscore format. Cannot be edited after creation."
      />
      <TextField
        label="Common Name Nigeria"
        required
        value={form.common_name_nigeria}
        onChange={set('common_name_nigeria')}
      />
      <TextField
        label="Scientific Name"
        required
        value={form.scientific_name}
        onChange={set('scientific_name')}
        placeholder="e.g. Genus species"
      />
      <SegmentedField
        label="Species Category"
        required
        value={form.species_category}
        onChange={set('species_category')}
        options={SPECIES_CATEGORIES}
      />
      <BooleanField
        label="Sex Disaggregation Required"
        required
        value={form.sex_disaggregation_required}
        onChange={set('sex_disaggregation_required')}
      />
      <SelectField
        label="Mandate Status"
        required
        value={form.mandate_status}
        onChange={set('mandate_status')}
        options={MANDATE_STATUSES}
        placeholder="Select Status"
      />
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-800">
        Validation status and mandate decision reference are assigned by the backend after this
        record is created.
      </div>
    </Modal>
  );
}
